<script setup lang="ts">
import { computed } from 'vue';
import AppSpinner from '@/shared/ui/AppSpinner.vue';

/**
 * PQ-002 — a busy button is disabled while its action is in flight, which is what
 * prevents the double submit that turns into a duplicate record.
 * PQ-007 — minimum touch target of 44px on every variant.
 */
const props = withDefaults(
  defineProps<{
    variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
    type?: 'button' | 'submit';
    busy?: boolean;
    disabled?: boolean;
    block?: boolean;
  }>(),
  { variant: 'primary', type: 'button', busy: false, disabled: false, block: false },
);

/**
 * The primary action is graphite, not brass. Brass is the accent that marks *where you
 * are* — the active tab, the selected chip, the progress fill. Spending it on every submit
 * button would leave nothing to mark position with.
 */
const VARIANTS = {
  primary: 'bg-ink text-surface-muted hover:bg-ink-soft disabled:bg-ink-muted',
  secondary:
    'bg-surface text-ink-soft border border-border hover:border-brand-300 disabled:text-ink-muted',
  ghost: 'bg-transparent text-brand-700 hover:bg-brand-50 disabled:text-ink-muted',
  danger: 'bg-danger-600 text-white hover:brightness-110 disabled:opacity-50',
} as const;

const isInactive = computed(() => props.disabled || props.busy);
</script>

<template>
  <button
    :type="type"
    :disabled="isInactive"
    :aria-busy="busy"
    :class="[
      VARIANTS[variant],
      block ? 'w-full' : '',
      'inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-4 py-2',
      'text-sm font-medium transition-colors disabled:cursor-not-allowed',
    ]"
  >
    <AppSpinner
      v-if="busy"
      size="sm"
      label="Procesando"
    />
    <slot />
  </button>
</template>
